import { ArrowDownRight, Sparkles } from 'lucide-react';
import { APP_META } from '../config';
import StoreBadges from './StoreBadges';
import LiveRatesTicker from './LiveRatesTicker';

/**
 * Above-the-fold hero. Layered radial gradients (defined in tailwind.config)
 * over a faint dot grid produce the soft cyan/blue glow without any JS
 * animation budget. The phone mock on the right is a CSS-only frame
 * wrapping the splash poster so we always have a hero image even before
 * real screenshots land.
 */
export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-hero-radial bg-[length:200%_200%] animate-gradient-shift">
      <div className="absolute inset-0 bg-grid pointer-events-none" />

      <nav className="relative z-10 container-page flex items-center justify-between py-6">
        <a href="#" className="flex items-center gap-2.5 font-display text-lg font-bold">
          <img src="/favicon.png" alt="" className="h-8 w-8" />
          <span className="tracking-tight">{APP_META.name}</span>
        </a>
        <div className="hidden items-center gap-7 text-sm font-medium text-slate-300 md:flex">
          <a href="#features" className="hover:text-white transition-colors">
            Features
          </a>
          <a href="#screenshots" className="hover:text-white transition-colors">
            Screenshots
          </a>
          <a
            href="#download"
            className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-1.5 text-brand-cyan hover:bg-brand-cyan/20 transition-colors"
          >
            Download
          </a>
        </div>
      </nav>

      <div className="relative z-10 container-page grid grid-cols-1 items-center gap-16 pb-24 pt-12 md:grid-cols-12 md:pb-32 md:pt-20">
        <div className="md:col-span-7 animate-fade-up">
          <div className="pill mb-6">
            <Sparkles className="h-3.5 w-3.5 text-brand-cyan" />
            <span>Now tracking 8 currencies in real time</span>
          </div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            <span className="text-white">Live Rates.</span>{' '}
            <span className="bg-gradient-to-r from-brand-cyan via-cyan-300 to-brand-blue bg-clip-text text-transparent">
              Smarter Decisions.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300 sm:text-xl">
            Dinar Pulse brings live Libyan Dinar exchange rates, per-bank
            quotes, smart price alerts, and a beautiful converter — all in
            one fast app. Free on Android and iOS.
          </p>

          <div id="download" className="mt-10 scroll-mt-24">
            <StoreBadges />
          </div>

          <div className="mt-10">
            <LiveRatesTicker />
          </div>
        </div>

        {/* Phone mock */}
        <div className="md:col-span-5 flex justify-center md:justify-end">
          <div className="animate-float">
            <div className="relative rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-white/10 to-white/[0.02] p-2 shadow-glow backdrop-blur-sm">
              <div className="absolute left-1/2 top-2 z-20 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-black/70" />
              <img
                src="/splash.jpg"
                alt="Dinar Pulse on a phone"
                className="h-[520px] w-[260px] rounded-[2rem] object-cover sm:h-[600px] sm:w-[300px]"
              />
            </div>
            <a
              href="#features"
              className="mt-6 flex items-center justify-center gap-1 text-xs font-medium text-slate-400 hover:text-white transition-colors"
            >
              Scroll to features <ArrowDownRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
